import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { IdentityClient, IdentityRequestError } from '@/identity/client';
import { installResumeHandlers } from '@/app/resumeOnVisible';
import { db } from '@/store/db';
import { uuidToHex, newUuid, type Hex } from '@/store/ids';
import {
  clearLocalAccountData,
  countUnsentOperations,
  ensureInstallationId,
  getSignedInAccount,
} from '@/store/localStore';
import { WorkerClient } from '@/transport/workerClient';
import type { AccountRecord } from '@/store/types';
import type { ConnectionPhase } from '@/transport/workerProtocol';

/**
 * Session ownership: the access token, the identity client, and the worker.
 *
 * The access token lives in a ref and never reaches browser storage — Section
 * 12 requires exactly that, and a token in local storage is readable by any
 * script that ever runs on the page. The refresh credential is a cookie this
 * code cannot read at all, which is why "restore my session on reload" is a
 * call to `/v1/auth/refresh` rather than a read from storage.
 */

export interface SessionValue {
  account: AccountRecord | null;
  phase: ConnectionPhase;
  /** True until the initial refresh attempt has settled, so the shell can wait. */
  restoring: boolean;
  identity: IdentityClient;
  worker: WorkerClient | null;
  installationId: string;
  signIn: (account: AccountRecord, accessToken: string) => Promise<void>;
  signOut: () => Promise<void>;
  unsentOperationCount: () => Promise<number>;
  /** CR-01 §5: asks for one accelerated round, inside the shared retry budget. */
  retryNow: () => void;
}

const SessionContext = createContext<SessionValue | null>(null);

/**
 * Where the identity origin and the transport host come from.
 *
 * Section 11 names PUBLIC_WEB_ORIGIN, WSS_HOST and the core HTTPS listener as
 * environment configuration. A client-rendered bundle has no server to read
 * those from at request time, so they are Vite build-time variables with
 * same-origin defaults that work against a local development proxy.
 */
const env = import.meta.env as Readonly<Record<string, string | undefined>>;

const IDENTITY_ORIGIN = env['VITE_IDENTITY_ORIGIN'] ?? window.location.origin;
const WSS_URL =
  env['VITE_WSS_URL'] ??
  `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/ws`;

export function SessionProvider({ children }: { children: ReactNode }) {
  const [account, setAccount] = useState<AccountRecord | null>(null);
  const [phase, setPhase] = useState<ConnectionPhase>('idle');
  const [restoring, setRestoring] = useState(true);
  const [installationId, setInstallationId] = useState('');
  const [worker, setWorker] = useState<WorkerClient | null>(null);

  /**
   * CR-01 §6.3: a tab that was hidden or frozen must not reuse a stale Connected
   * display. One budgeted attempt per resume, never a replay of every timer that
   * came due while it was asleep.
   */
  useEffect(() => {
    if (!worker) return;
    return installResumeHandlers(worker);
  }, [worker]);

  const accessTokenRef = useRef<string | null>(null);
  const identity = useMemo(() => new IdentityClient(IDENTITY_ORIGIN), []);
  const workerRef = useRef<WorkerClient | null>(null);

  /**
   * Mints a connection ticket for the worker, refreshing the access token once
   * if it has expired.
   *
   * One retry, not a loop: if a freshly refreshed token is also rejected the
   * session is genuinely gone, and looping would hammer the identity service
   * while showing the user nothing.
   */
  const mintTicket = useCallback(async (): Promise<string> => {
    const attempt = async (token: string): Promise<string> => {
      const result = await identity.createConnectionTicket(token);
      return result.ticket;
    };

    const current = accessTokenRef.current;
    if (current) {
      try {
        return await attempt(current);
      } catch (error) {
        if (!(error instanceof IdentityRequestError) || error.status !== 401) {
          throw error;
        }
      }
    }

    const refreshed = await identity.refresh(newUuid());
    accessTokenRef.current = refreshed.accessToken;
    return attempt(refreshed.accessToken);
  }, [identity]);

  const teardownWorker = useCallback(() => {
    const existing = workerRef.current;
    if (!existing) {
      return;
    }
    existing.stop();
    existing.terminate();
    workerRef.current = null;
    setWorker(null);
  }, []);

  const startWorker = useCallback(
    (record: AccountRecord) => {
      teardownWorker();
      const client = new WorkerClient({
        mintTicket,
        onUnauthenticated: () => {
          // The credentials are gone. The worker has already stopped; the page
          // clears its token so nothing tries again with a value it knows is
          // dead, and the sign-in screen is shown.
          accessTokenRef.current = null;
          setPhase('unauthenticated');
        },
      });
      client.onPhase(setPhase);
      client.start(WSS_URL, {
        userId: record.id,
        deviceId: record.deviceId,
        sessionId: record.sessionId,
      });
      workerRef.current = client;
      setWorker(client);
    },
    [mintTicket, teardownWorker],
  );

  // Restore a session on load. The refresh cookie survives a reload; the access
  // token deliberately does not.
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const id = await ensureInstallationId();
        if (!cancelled) {
          setInstallationId(id);
        }

        const stored = await getSignedInAccount();
        if (!stored) {
          return;
        }
        const refreshed = await identity.refresh(newUuid());
        if (cancelled) {
          return;
        }
        accessTokenRef.current = refreshed.accessToken;
        setAccount(stored);
        startWorker(stored);
      } catch {
        // No usable session. The stored account row is left alone: it carries
        // the device identity that pending operations belong to, and Section 05
        // requires those to stay associated with their original device rather
        // than being orphaned by a failed refresh.
        if (!cancelled) {
          setPhase('unauthenticated');
        }
      } finally {
        if (!cancelled) {
          setRestoring(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [identity, startWorker]);

  useEffect(() => teardownWorker, [teardownWorker]);

  const signIn = useCallback(
    async (record: AccountRecord, accessToken: string) => {
      accessTokenRef.current = accessToken;
      await db.accounts.put(record);
      setAccount(record);
      startWorker(record);
    },
    [startWorker],
  );

  const signOut = useCallback(async () => {
    const token = accessTokenRef.current;
    teardownWorker();
    if (token) {
      try {
        await identity.logout(token);
      } catch {
        // Revocation is best-effort from the client's side. The local caches
        // are cleared regardless, and the server expires the session anyway.
      }
    }
    accessTokenRef.current = null;
    await clearLocalAccountData();
    setAccount(null);
    setPhase('idle');
  }, [identity, teardownWorker]);

  const value = useMemo<SessionValue>(
    () => ({
      account,
      phase,
      restoring,
      identity,
      worker,
      installationId,
      signIn,
      signOut,
      unsentOperationCount: countUnsentOperations,
      // Routed to the worker, which owns the connection. The page never dials:
      // §3 allows exactly one controller and a button that opened its own socket
      // would be a second one.
      retryNow: () => worker?.retryNow(),
    }),
    [account, phase, restoring, identity, worker, installationId, signIn, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) {
    throw new Error('useSession must be used inside SessionProvider');
  }
  return value;
}

/** Builds the stored account row from an identity verification result. */
export function accountFromVerify(input: {
  accountId: string;
  displayName: string;
  username: string | null;
  deviceId: string;
  sessionId: string;
  installationId: string;
  phoneE164: string;
}): AccountRecord {
  return {
    id: uuidToHex(input.accountId) as Hex,
    deviceId: uuidToHex(input.deviceId) as Hex,
    sessionId: uuidToHex(input.sessionId) as Hex,
    installationId: input.installationId,
    displayName: input.displayName,
    username: input.username,
    phoneE164: input.phoneE164,
    lastSignedInAt: Date.now(),
  };
}
